import { useNavigate, useParams } from "react-router";
import { AccountMenu } from "@/components/common/AccountMenu";
import type { IconName } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { NavItemButton } from "@/components/common/NavItemButton";
import { CreditsDashboardPage } from "@/components/finance/CreditsDashboardPage";
import { FinancePage } from "@/components/finance/FinancePage";
import { InfraPage } from "@/components/infra/InfraPage";
import { PerformancePage } from "@/components/infra/PerformancePage";
import { StartFailuresPage } from "@/components/infra/StartFailuresPage";
import { StoragePage } from "@/components/infra/StoragePage";
import { TOP_GAP } from "@/lib/pane-styles";
import { OrganizationsTab } from "./OrganizationsTab";
import { UsersTab } from "./UsersTab";

export type AdminTab =
	| "organizations"
	| "users"
	| "finance"
	| "credits"
	| "infra"
	| "storage"
	| "start-failures"
	| "performance";

const TABS: { id: AdminTab; icon: IconName; label: string; path: string }[] = [
	{ id: "organizations", icon: "users", label: "組織", path: "/admin/organizations" },
	{ id: "users", icon: "user", label: "ユーザー", path: "/admin/users" },
	{ id: "finance", icon: "creditCard", label: "財務", path: "/admin/finance" },
	{ id: "credits", icon: "grid", label: "クレジット", path: "/admin/credits" },
	{ id: "infra", icon: "chip", label: "ノード", path: "/infra" },
	{ id: "storage", icon: "database", label: "ストレージ", path: "/admin/storage" },
	{
		id: "start-failures",
		icon: "alertTriangle",
		label: "環境の起動失敗",
		path: "/admin/start-failures",
	},
	{ id: "performance", icon: "clock", label: "パフォーマンス", path: "/admin/performance" },
];

/**
 * SuperAdmin 向けの社内管理ページ (/admin)。左のナビでタブを切り替える。
 * ノードのタブは /infra で開く (tab で指定する)。
 */
export function AdminPage({ tab }: { tab?: AdminTab }) {
	const navigate = useNavigate();
	const { tab: tabParam } = useParams<{ tab: string }>();
	const activeTab = tab ?? TABS.find((t) => t.id === tabParam)?.id ?? TABS[0].id;

	return (
		<div
			data-testid="admin-page"
			style={{ display: "flex", height: "100vh", background: "var(--surface-1)" }}
		>
			<nav
				style={{
					flex: "none",
					width: 200,
					display: "flex",
					flexDirection: "column",
					gap: 4,
					padding: `${TOP_GAP + 8}px 8px 8px`,
					borderRight: "var(--pane-header-border)",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 6, height: 32, marginBottom: 4 }}>
					<IconButton
						icon="arrowL"
						title="Izanagi へ戻る"
						data-testid="admin-back"
						size={24}
						onClick={() => navigate("/")}
					/>
					<h1
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							fontWeight: 600,
							letterSpacing: "var(--tracking-snug)",
							color: "var(--fg-1)",
						}}
					>
						管理
					</h1>
				</div>
				{TABS.map((t) => (
					<NavItemButton
						key={t.id}
						icon={t.icon}
						label={t.label}
						compact
						selected={activeTab === t.id}
						onClick={() => navigate(t.path, { replace: true })}
						testId={`admin-tab-${t.id}`}
					/>
				))}
				<div style={{ marginTop: "auto" }}>
					<AccountMenu />
				</div>
			</nav>
			<main style={{ flex: 1, minWidth: 0 }}>
				{activeTab === "organizations" && <OrganizationsTab />}
				{activeTab === "users" && <UsersTab scope="all" />}
				{activeTab === "infra" && <InfraPage />}
				{activeTab === "storage" && <StoragePage />}
				{activeTab === "finance" && <FinancePage />}
				{activeTab === "credits" && <CreditsDashboardPage />}
				{activeTab === "start-failures" && <StartFailuresPage />}
				{activeTab === "performance" && <PerformancePage />}
			</main>
		</div>
	);
}
